"use client";

import { useAuth } from "@/context/auth";
import { useWallet } from "@/context/wallet";
import { $api } from "@/lib/api-client";

export interface MaintenanceBypass {
  /** True when this visitor may use the site despite the freeze. */
  isAllowed: boolean;
  /**
   * True once we know enough to act. Callers MUST NOT redirect while this is
   * false, or an allowlisted visitor gets bounced before their wallet is known.
   */
  isResolved: boolean;
}

/**
 * Whether maintenance mode should be transparent for the current visitor.
 *
 * Both stores read here are Zustand (`create()`), not React context, so this
 * works from MaintenanceGuard's existing position ABOVE <WalletLayer>. That is
 * why the provider tree did not need restructuring for this feature.
 */
export function useMaintenanceBypass(
  isMaintenanceEnabled: boolean,
): MaintenanceBypass {
  const authWallet = useAuth((s) => s.user?.wallet_address);
  const connectedWallet = useWallet((s) => s.address);
  const fullConfigReady = useWallet((s) => s.fullConfigReady);
  const fullConfigFailed = useWallet((s) => s.fullConfigFailed);

  // A logged-in wallet is the stronger signal: it survives the wagmi config
  // swap and does not depend on the connector having rehydrated.
  const wallet = authWallet ?? connectedWallet;

  const { data, isPending } = $api.useQuery(
    "get",
    "/maintenance-allowlist/check/{wallet}",
    { params: { path: { wallet: wallet ?? "" } } },
    {
      // Only ask during a freeze, and only once we have a wallet to ask about.
      enabled: isMaintenanceEnabled && Boolean(wallet),
      staleTime: 60_000,
    },
  );

  if (!isMaintenanceEnabled) return { isAllowed: false, isResolved: true };

  // Until the full wagmi config has mounted, an absent address means "not known
  // yet", not "not connected" — see the comment on fullConfigReady in
  // src/context/wallet.ts. Treating it as absent is what would cause a spurious
  // redirect.
  //
  // fullConfigFailed is the escape hatch: if that chunk can never load, waiting
  // is unbounded and NOBODY would be redirected to /maintenance during a
  // freeze. Fall through on failure and decide with whatever wallet we have
  // (typically the persisted auth one, which does not depend on wagmi).
  if (!fullConfigReady && !fullConfigFailed)
    return { isAllowed: false, isResolved: false };

  // No wallet at all: nothing to look up, and the answer is settled.
  if (!wallet) return { isAllowed: false, isResolved: true };

  return { isAllowed: data?.data?.allowed === true, isResolved: !isPending };
}
